import React, { useState, useEffect } from 'react';
import { api } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Sparkles, Users, MapPin, Clock, Trash2, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';

const OpsTasks = () => {
  const [tasks, setTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [recommendationsDialog, setRecommendationsDialog] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    estimated_hours: '',
    priority: 'medium',
    required_skills: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tasksRes, teamRes] = await Promise.all([
        api.get('/ops/tasks'),
        api.get('/ops/team?status=active')
      ]);
      setTasks(tasksRes.data || []);
      setTeamMembers(teamRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async () => {
    if (!formData.title || !formData.estimated_hours) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      await api.post('/ops/tasks', {
        ...formData,
        estimated_hours: parseFloat(formData.estimated_hours)
      });
      toast.success('Task created successfully');
      setCreateDialogOpen(false);
      setFormData({
        title: '',
        description: '',
        location: '',
        estimated_hours: '',
        priority: 'medium',
        required_skills: []
      });
      fetchData();
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Failed to create task');
    }
  };

  const handleGetRecommendations = async (task) => {
    try {
      const response = await api.get(`/ops/tasks/${task.id}/recommendations`);
      setRecommendations(response.data.recommendations || []);
      setRecommendationsDialog(task);
    } catch (error) {
      console.error('Error getting recommendations:', error);
      toast.error('Failed to get recommendations');
    }
  };

  const handleAssignTask = async (taskId, memberIds) => {
    try {
      await api.post(`/ops/tasks/${taskId}/assign`, memberIds);
      toast.success('Task assigned successfully');
      setRecommendationsDialog(null);
      fetchData();
    } catch (error) {
      console.error('Error assigning task:', error);
      toast.error('Failed to assign task');
    }
  };

  const handleDeleteClick = (task) => {
    setTaskToDelete(task);
    setDeleteDialogOpen(true);
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;
    
    try {
      await api.delete(`/ops/tasks/${taskToDelete.id}`);
      toast.success('Task deleted successfully');
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'bg-red-100 text-red-700 border-red-300',
      high: 'bg-orange-100 text-orange-700 border-orange-300',
      medium: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      low: 'bg-blue-100 text-blue-700 border-blue-300'
    };
    return colors[priority] || colors.medium;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Task Management</h2>
          <p className="text-slate-600 text-sm mt-1">Create, assign, and track operational tasks</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} className="bg-[#247C75] hover:bg-[#1d6259]">
          <Plus className="h-4 w-4 mr-2" />
          New Task
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {['unassigned', 'assigned', 'in_progress'].map(status => (
          <Card key={status}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium capitalize">
                {status.replace('_', ' ')} ({tasks.filter(t => t.status === status).length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {tasks.filter(t => t.status === status).map(task => (
                <div key={task.id} className="p-3 rounded-lg border border-slate-200 hover:shadow-md transition-shadow relative group">
                  {/* Delete button - shows on hover */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
                    onClick={() => handleDeleteClick(task)}
                  >
                    <Trash2 className="h-3 w-3 text-red-600" />
                  </Button>

                  <div className="flex items-start justify-between mb-2 pr-8">
                    <h4 className="text-sm font-semibold text-slate-900">{task.title}</h4>
                    <Badge className={getPriorityColor(task.priority)}>{task.priority}</Badge>
                  </div>
                  {task.description && (
                    <p className="text-xs text-slate-600 mb-2">{task.description}</p>
                  )}
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    {task.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />{task.location}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />{task.estimated_hours}h
                    </span>
                    {task.assigned_to?.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" />{task.assigned_to.length}
                      </span>
                    )}
                  </div>
                  {status === 'unassigned' && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="w-full mt-2 text-[#247C75] border-[#247C75]"
                      onClick={() => handleGetRecommendations(task)}
                    >
                      <Sparkles className="h-3 w-3 mr-1" />
                      AI Recommendations
                    </Button>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <textarea className="w-full min-h-[80px] rounded-md border border-slate-300 p-2" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Location</Label>
                <Input value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Estimated Hours *</Label>
                <Input type="number" step="0.5" value={formData.estimated_hours} onChange={(e) => setFormData({...formData, estimated_hours: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateTask} className="bg-[#247C75] hover:bg-[#1d6259]">Create Task</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recommendations Dialog */}
      <Dialog open={!!recommendationsDialog} onOpenChange={() => setRecommendationsDialog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-500" />
              AI-Powered Recommendations
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium text-slate-900">{recommendationsDialog?.title}</p>
              <p className="text-xs text-slate-600 mt-1">{recommendationsDialog?.estimated_hours}h • {recommendationsDialog?.priority} priority</p>
            </div>
            {recommendations.length === 0 ? (
              <p className="text-center text-slate-500 py-4">No recommendations available</p>
            ) : (
              <div className="space-y-2">
                {recommendations.map((rec, idx) => (
                  <div key={idx} className="p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer" onClick={() => handleAssignTask(recommendationsDialog.id, [rec.team_member_id])}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{rec.team_member_name}</p>
                        <p className="text-xs text-slate-600">{rec.reason}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-[#247C75]">{rec.confidence_score}%</div>
                        <p className="text-xs text-slate-500">match</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Task</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{taskToDelete?.title}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteTask}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OpsTasks;